import { api } from './client';
import type { TierName } from './seasons';
import type { Difficulty } from './puzzles';

export interface PlayerDifficultyStats {
  difficulty: Difficulty;
  played: number;
  won: number;
}

/** One board's showing THIS calendar month. `best_position` is the highest
 *  daily-leaderboard placing reached on it, or null when they have not
 *  finished one — rendered as a dash, never "0th". */
export interface PlayerMonthlyFinish extends PlayerDifficultyStats {
  best_position: number | null;
}

/** Mirrors the server's `PublicProfile` (server/src/services/profile.service.ts)
 *  field for field — that type is itself an explicit allowlist, so this
 *  client type must never grow fields the server doesn't send. */
export interface PlayerProfile {
  leaderboard_name: string;
  /** 'YYYY-MM' — month granularity only, never a timestamp. */
  playing_since: string;
  /** Null for a player who hasn't played this season. */
  season: { key: string; points: number; rank: number | null; tier: TierName | null } | null;
  medals: { platinum: number; gold: number; silver: number; bronze: number };
  /** Season keys the player won Platinum in, oldest first. */
  platinum_seasons: string[];
  all_time: { games: number; wins: number; best_streak: number };
  by_difficulty: PlayerDifficultyStats[];
  /** Always all three difficulties, zeroed where unplayed. */
  this_month: PlayerMonthlyFinish[];
}

/**
 * Public player profile by leaderboard name — signed-out visitors can call
 * this (spec: the endpoint never requires auth). A 404 comes back as
 * `{ error_code: 'PLAYER_NOT_FOUND' }`; use `apiErrorCode` from `./client`
 * to detect it rather than matching on `err.response.status` alone.
 *
 * The name is URL-encoded: pseudonyms contain spaces and '#'
 * (e.g. "Velvet Quokka #4821"), which would otherwise corrupt the path or
 * be parsed as a URL fragment.
 */
export async function fetchPlayerProfile(leaderboardName: string): Promise<PlayerProfile> {
  const res = await api.get(`/api/players/${encodeURIComponent(leaderboardName)}`);
  return res.data;
}
